/**
 * TopicNewsStrip — reusable "Latest news" bar for the top of every
 * vertical/topic page (Sports, Education, Government, Health, Travel,
 * Nature, Weather, Marketplace, Jobs, etc.).
 *
 * Fetches from /api/news and renders 4-6 compact cards. Location-
 * aware — passes `useLocality()`'s country if available so
 * international users see local coverage first.
 *
 * Props:
 * - `topic`   — Display label ("Sports news", "Health news", …).
 * - `category` — Direct NEWS_CATEGORIES match (e.g. "Sports", "Health").
 * - `q`       — Free-text keyword for topics without a direct
 *               category ("education", "travel", "jobs", …).
 * - `limit`   — Number of headlines to fetch (default 5).
 * - `href`    — Where the "See more" link points (default /things-to-know).
 * - `testId`  — data-testid override (default `topic-news-strip`).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ExternalLink, ArrowRight, MapPin } from "lucide-react";
import { http } from "@/lib/api";
import { useLocality } from "@/contexts/LocalityContext";

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function TopicNewsStrip({
  topic,
  category,
  q,
  limit = 5,
  href = "/things-to-know",
  testId = "topic-news-strip",
}) {
  const { locality } = useLocality();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { limit };
    if (category) params.category = category;
    if (q)        params.q        = q;
    if (locality?.country) params.country = locality.country;

    let cancelled = false;
    const fetchNews = async () => {
      // Try location-scoped first; if empty, fall back to worldwide.
      let res = await http.get("/news", { params }).then(r => r.data).catch(() => null);
      if ((!res?.items || res.items.length === 0) && params.country) {
        const { country, ...rest } = params;
        res = await http.get("/news", { params: rest }).then(r => r.data).catch(() => null);
      }
      if (!cancelled) {
        setItems(res?.items || []);
        setLoading(false);
      }
    };
    fetchNews();
    return () => { cancelled = true; };
  }, [category, q, limit, locality?.country]);

  if (!loading && items.length === 0) return null;

  const scopeLabel = locality?.country ? locality.country : "Worldwide";

  return (
    <section
      className="mb-8"
      data-testid={testId}
      aria-label={`${topic} news`}
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full grid place-items-center shrink-0"
               style={{ background: "var(--wofy-surface)" }} aria-hidden>
            <Newspaper className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">
              LATEST {topic.toUpperCase()} NEWS · {scopeLabel.toUpperCase()}
            </div>
            <div className="text-xs opacity-60 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3"/>{locality?.city ? `${locality.city}, ${locality.country}` : "Global feed"}
            </div>
          </div>
        </div>
        <Link
          to={href}
          className="text-xs wofy-link inline-flex items-center gap-0.5"
          data-testid={`${testId}-see-more`}
        >
          See more news <ArrowRight className="w-3 h-3"/>
        </Link>
      </div>

      {loading ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="wofy-card !p-3 h-24 animate-pulse" style={{ background: "rgba(0,0,0,0.04)" }} />
          ))}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {items.slice(0, Math.min(limit, 6)).map((n) => (
            <a
              key={n.id || n.url}
              href={n.url}
              target="_blank"
              rel="noopener noreferrer"
              className="wofy-card !p-3 hover:shadow-md transition block group"
              data-testid={`${testId}-item`}
            >
              <div className="flex items-start gap-3">
                {n.image_url && (
                  <img
                    src={n.image_url}
                    alt=""
                    className="w-14 h-14 object-cover rounded-md shrink-0"
                    loading="lazy"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-0.5 truncate">
                    {n.source_name || n.category || "News"} · {relTime(n.published_at)}
                  </div>
                  <div className="text-sm font-medium leading-snug line-clamp-2 group-hover:underline">
                    {n.title}
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 opacity-40 shrink-0 mt-1"/>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
